import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton,
    InputAdornment, LinearProgress, MenuItem, Paper, Stack, Switch, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, LockReset as LockResetIcon, Autorenew as AutorenewIcon } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { ManagedUser, UserRole } from '../../types/auth';
import { apiErrorMessage, hrService } from '../../services/hrService';

const ROLES = [UserRole.COLABORADOR, UserRole.GESTOR, UserRole.RH, UserRole.DIRETOR];

/** Senha temporária que já atende à política (maiúscula, minúscula, número e especial). */
const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
    const random = new Uint32Array(10);
    window.crypto.getRandomValues(random);
    return `Q!${Array.from(random, (n) => chars[n % chars.length]).join('')}7a`;
};

type DialogState =
    | { kind: 'create' }
    | { kind: 'edit'; user: ManagedUser }
    | { kind: 'reset'; user: ManagedUser }
    | null;

const ColaboradoresList: React.FC = () => {
    const { user: me } = useAuth();
    const isDirector = me?.role === UserRole.DIRETOR;
    const [users, setUsers] = useState<ManagedUser[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [search, setSearch] = useState('');
    const [showInactive, setShowInactive] = useState(false);
    const [dialog, setDialog] = useState<DialogState>(null);
    const [form, setForm] = useState({ name: '', email: '', role: UserRole.COLABORADOR as string, password: '', is_active: true });
    const [formError, setFormError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            setUsers(await hrService.listUsers());
            setError(null);
        } catch (err) {
            setError(apiErrorMessage(err, 'Erro ao carregar os usuários.'));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const visible = useMemo(() => users.filter((u) =>
        (showInactive || u.is_active) &&
        `${u.name} ${u.email} ${u.role}`.toLowerCase().includes(search.trim().toLowerCase())
    ), [users, search, showInactive]);

    const openDialog = (state: DialogState) => {
        setFormError(null);
        if (state?.kind === 'create') {
            setForm({ name: '', email: '', role: UserRole.COLABORADOR, password: generatePassword(), is_active: true });
        } else if (state?.kind === 'edit') {
            setForm({ ...form, name: state.user.name, email: state.user.email, role: state.user.role, is_active: state.user.is_active });
        } else if (state?.kind === 'reset') {
            setForm({ ...form, password: generatePassword() });
        }
        setDialog(state);
    };

    const handleSave = async () => {
        if (!dialog) return;
        try {
            setSaving(true);
            setFormError(null);
            if (dialog.kind === 'create') {
                await hrService.createUser({ name: form.name, email: form.email, role: form.role, password: form.password });
                setSuccess(`Usuário criado. Repasse a senha temporária a ${form.name}: a troca é obrigatória no primeiro acesso.`);
            } else if (dialog.kind === 'edit') {
                await hrService.updateUser(dialog.user.id, { name: form.name, role: form.role, is_active: form.is_active });
                setSuccess('Usuário atualizado.');
            } else {
                await hrService.resetPassword(dialog.user.id, form.password);
                setSuccess(`Senha redefinida. Repasse a nova senha temporária a ${dialog.user.name}.`);
            }
            setDialog(null);
            load();
        } catch (err) {
            setFormError(apiErrorMessage(err, 'Erro ao salvar.'));
        } finally {
            setSaving(false);
        }
    };

    const isSelf = dialog && dialog.kind !== 'create' && dialog.user.id === me?.id;
    // Só Diretor concede ou mexe em quem é Diretor (o backend também valida)
    const roleOptions = ROLES.filter((r) => isDirector || r !== UserRole.DIRETOR);
    const lockedByRole = dialog?.kind === 'edit' && dialog.user.role === UserRole.DIRETOR && !isDirector;

    return (
        <Box sx={{ p: { xs: 0, sm: 1 } }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={2}>
                <Typography variant="h5">Colaboradores</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => openDialog({ kind: 'create' })}>
                    Novo usuário
                </Button>
            </Box>
            <Typography variant="body2" color="text.secondary" mb={2}>
                Os acessos ao sistema são criados aqui pelo RH ou pela diretoria. Cada pessoa recebe uma senha temporária
                e precisa trocá-la no primeiro acesso.
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

            <Stack direction="row" spacing={2} mb={2} flexWrap="wrap" useFlexGap alignItems="center">
                <TextField size="small" label="Buscar" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ flex: '1 1 220px' }} />
                <FormControlLabel control={<Switch checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />}
                    label="Mostrar desativados" />
            </Stack>

            {loading && <LinearProgress sx={{ mb: 1 }} />}
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Nome</TableCell>
                            <TableCell>E-mail</TableCell>
                            <TableCell>Perfil</TableCell>
                            <TableCell>Situação</TableCell>
                            <TableCell align="right">Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {visible.map((u) => (
                            <TableRow key={u.id} hover>
                                <TableCell>{u.name}{u.id === me?.id ? ' (você)' : ''}</TableCell>
                                <TableCell>{u.email}</TableCell>
                                <TableCell>{u.role}</TableCell>
                                <TableCell>
                                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                        <Chip size="small" label={u.is_active ? 'Ativo' : 'Desativado'} color={u.is_active ? 'success' : 'default'} />
                                        {u.must_change_password && <Chip size="small" variant="outlined" label="Aguardando 1º acesso" />}
                                    </Stack>
                                </TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                    <Tooltip title="Editar">
                                        <IconButton size="small" aria-label={`Editar ${u.name}`} onClick={() => openDialog({ kind: 'edit', user: u })}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Redefinir senha">
                                        <span>
                                            <IconButton size="small" aria-label={`Redefinir senha de ${u.name}`}
                                                disabled={u.role === UserRole.DIRETOR && !isDirector}
                                                onClick={() => openDialog({ kind: 'reset', user: u })}>
                                                <LockResetIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                        {!loading && visible.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={5} align="center">Nenhum usuário encontrado.</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={!!dialog} onClose={() => setDialog(null)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {dialog?.kind === 'create' ? 'Novo usuário' : dialog?.kind === 'edit' ? `Editar ${dialog.user.name}` : `Redefinir senha de ${dialog?.user.name}`}
                </DialogTitle>
                <DialogContent>
                    {formError && <Alert severity="error" sx={{ mt: 1 }}>{formError}</Alert>}
                    {lockedByRole && <Alert severity="info" sx={{ mt: 1 }}>Somente um Diretor pode alterar outro Diretor.</Alert>}
                    {dialog?.kind !== 'reset' && (
                        <>
                            <TextField fullWidth margin="normal" label="Nome" value={form.name} required disabled={lockedByRole}
                                onChange={(e) => setForm({ ...form, name: e.target.value })} />
                            <TextField fullWidth margin="normal" label="E-mail" type="email" value={form.email} required
                                disabled={dialog?.kind === 'edit'} onChange={(e) => setForm({ ...form, email: e.target.value })}
                                helperText={dialog?.kind === 'edit' ? 'O e-mail é o login e não pode ser alterado aqui' : ''} />
                            <TextField select fullWidth margin="normal" label="Perfil de acesso" value={form.role}
                                disabled={!!isSelf || lockedByRole} onChange={(e) => setForm({ ...form, role: e.target.value })}
                                helperText={isSelf ? 'Você não pode alterar o seu próprio perfil' : ''}>
                                {(roleOptions.includes(form.role as UserRole) ? roleOptions : [...roleOptions, form.role as UserRole]).map((r) => (
                                    <MenuItem key={r} value={r}>{r}</MenuItem>
                                ))}
                            </TextField>
                        </>
                    )}
                    {dialog?.kind === 'edit' && (
                        <FormControlLabel sx={{ mt: 1 }} label={form.is_active ? 'Acesso ativo' : 'Acesso desativado (não consegue entrar)'}
                            control={<Switch checked={form.is_active} disabled={!!isSelf || lockedByRole}
                                onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />} />
                    )}
                    {dialog?.kind !== 'edit' && (
                        <TextField fullWidth margin="normal" label="Senha temporária" value={form.password}
                            onChange={(e) => setForm({ ...form, password: e.target.value })}
                            helperText="Repasse à pessoa por um canal seguro. Ela será obrigada a trocá-la no primeiro acesso."
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Tooltip title="Gerar outra">
                                            <IconButton aria-label="Gerar outra senha" edge="end"
                                                onClick={() => setForm({ ...form, password: generatePassword() })}>
                                                <AutorenewIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </InputAdornment>
                                ),
                            }} />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialog(null)}>Cancelar</Button>
                    <Button variant="contained" onClick={handleSave}
                        disabled={saving || lockedByRole || (dialog?.kind !== 'reset' && (!form.name.trim() || !form.email.trim())) ||
                            (dialog?.kind !== 'edit' && !form.password)}>
                        Salvar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ColaboradoresList;
